import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { ASSETS_URL } from "../constants";
import useChatStore from '../store/useChatStore';
import DashboardProfileMenu from './dashboard/DashboardProfileMenu';
import { customerSignIn, fetchLeadsByMobile } from '../services/leadService';
import OtpVerificationModal from '../utility/OtpVerificationModal';
import showToast from '../utility/showToast';
import axios from 'axios';
import { CREDIT_API_URL } from '../constants';

const Header = () => {
    const [isMobile, setIsMobile] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [loginMobile, setLoginMobile] = useState('');
    const [showOtpModal, setShowOtpModal] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const whatsappRef = useRef(null);
    
    // Get customer and auth functions from store
    const customer = useChatStore((state) => state.customer);
    const setCustomer = useChatStore((state) => state.setCustomer);
    const setAuthToken = useChatStore((state) => state.setAuthToken);
    const logout = useChatStore((state) => state.logout);

    // Check if current path contains "/application"
    const isApplicationPage = location.pathname.includes('/application');

    // Navigation links
    const navigationLinks = [
        { name: 'ShowBiz', path: '/business-listing' },
        { name: 'Business Expansion', path: '/business-expansion' },
        { name: 'Supply Chain Financing', path: '/supply-chain-financing/anchor-registration' },
       
        
    ];

    // Languages - all options
    const languages = [
        { code: 'en', name: 'English', fullName: 'English' },
        // { code: 'hi', name: 'हिंदी', fullName: 'हिंदी' },
        // { code: 'mr', name: 'मराठी', fullName: 'मराठी' },
        // { code: 'gu', name: 'ગુજરાતી', fullName: 'ગુજરાતી' }
    ];

    useEffect(() => {
        // Detect if the user is on a mobile device
        const checkIfMobile = () => {
            setIsMobile(/Mobi|Android/i.test(navigator.userAgent));
        };

        checkIfMobile();
        window.addEventListener("resize", checkIfMobile);
        return () => window.removeEventListener("resize", checkIfMobile);
    }, []);

    // Ensure body is not clipping fixed elements
    useEffect(() => {
        const original = document.body.style.overflowX;
        document.body.style.overflowX = 'hidden';
        return () => {
            document.body.style.overflowX = original;
        };
    }, []);

    const handleWhatsAppClick = () => {
        const phoneNumber = '9662267474';
        const message = 'Hello! I would like to know more about your services.';
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
        
        // Open WhatsApp in a new tab/window
        window.open(whatsappUrl, '_blank');
    };

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const handleNavigationClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false); // Close mobile menu after navigation
    };

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const handleMouseEnter = () => {
        setDropdownOpen(true);
    };

    const handleMouseLeave = () => {
        setDropdownOpen(false);
    };

    // Login flow handlers
    const handleLoginClick = () => {
        setShowLoginModal(true);
    };

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        if (loginMobile.length !== 10) {
            showToast('Please enter a valid 10-digit mobile number', 'error');
            return;
        }

        try {
            // Call generate-otp for login flow
            const payload = {
                mobile: loginMobile,
                productType: 'UserLoginOtp'
            };
            const resp = await axios.post(`${CREDIT_API_URL}generate-otp`, payload, {
                headers: { 'Content-Type': 'application/json' }
            });

            if (resp.status === 200) {
                setShowLoginModal(false);
                setShowOtpModal(true);
                showToast('OTP sent to your mobile number', 'success');
            } else {
                showToast('Failed to send OTP. Please try again.', 'error');
            }
        } catch (err) {
            console.error('[Header] generate-otp error:', err);
            showToast(err.response?.data?.message || 'Failed to send OTP. Please try again.', 'error');
        }
    };

    const handleOtpSuccess = async () => {
        try {
            console.log('[Header] OTP verified, signing in customer...');
            const signInResult = await customerSignIn(loginMobile);

            if (signInResult.success && signInResult.token) {
                // Customer signed in successfully - check leads count
                console.log('[Header] Customer signed in, checking leads count...');
                setAuthToken(signInResult.token);
                
                // Fetch leads to determine routing
                const leadsResult = await fetchLeadsByMobile(loginMobile, signInResult.token);
                const leadsCount = leadsResult?.count || (Array.isArray(leadsResult?.data) ? leadsResult.data.length : 0);
                
                console.log('[Header] Leads count:', leadsCount);
                
                setShowOtpModal(false);
                setLoginMobile('');
                
                console.log('[Header] leadsResult (raw):', JSON.stringify(leadsResult));
                if (leadsCount > 1) {
                    // Multiple leads → Dashboard
                    console.log('[Header] Multiple leads found, redirecting to dashboard');
                    setCustomer(signInResult.customer);
                    showToast('Welcome back!', 'success');
                    navigate('/dashboard');
                } else if (leadsCount === 1) {
                    // Single lead → Bizzie chat to resume
                    console.log('[Header] Single lead found, redirecting to bizzie');
                    const singleLead = Array.isArray(leadsResult?.data) ? leadsResult.data[0] : leadsResult?.data;
                    
                    // Set customer with lead data
                    setCustomer({
                        ...signInResult.customer,
                        ...singleLead,
                        chatSessionId: singleLead?.chatSessionId || null,
                        loanRequestId: singleLead?.loanRequestId || singleLead?.id || null,
                    });
                    
                    // Set chat store state
                    useChatStore.setState({
                        chatSessionId: singleLead?.chatSessionId || null,
                        loanId: singleLead?.loanRequestId || singleLead?.id || null,
                    });
                    
                    showToast('Welcome back! Resuming your application...', 'success');
                    try {
                      console.log('[Header] Navigating to /bizzie now...');
                      navigate('/bizzie');
                    } catch (navErr) {
                      console.error('[Header] Navigation to /bizzie failed:', navErr);
                    }
                } else {
                    // No leads found (shouldn't happen if signIn succeeded, but handle it)
                    console.log('[Header] No leads found after sign-in');
                    setCustomer(signInResult.customer);
                    showToast('No applications found. Please create a new application.', 'info');
                }
            } else if (signInResult.isNewCustomer) {
                // New customer - no leads found
                showToast('No applications found. Please create a new application.', 'info');
                setShowOtpModal(false);
                setLoginMobile('');
            } else {
                throw new Error(signInResult.message || 'Failed to sign in');
            }
        } catch (err) {
            console.error('[Header] Login error:', err);
            showToast(err.message || 'Login failed. Please try again.', 'error');
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
        showToast('Logged out successfully', 'success');
    };

    const handleViewProfile = () => {
        navigate('/dashboard');
    };

    const handleSupport = () => {
        handleWhatsAppClick();
    };

    const handleResumeLatest = () => {
        navigate('/dashboard');
    };

    // Profile menu for logged in users
    const profileMenu = customer ? (
        <DashboardProfileMenu
            onResumeLatest={handleResumeLatest}
            onSupport={handleSupport}
            onViewProfile={handleViewProfile}
            onLogout={handleLogout}
        />
    ) : null;

    return (
        <>
            <nav className="custom-navbar sticky-top">
                <div className="navbar-container">
                    {/* Logo */}
                    <a className="navbar-brand" href="/">
                        <img
                            src="/images/growbizh-logo.png"
                            alt="GrowBizh"
                            className="logo-img"
                        />
                    </a>

                    {/* Navigation Links - Desktop */}
                    {!isMobile && !isApplicationPage && (
                        <div className="navbar-nav">
                            <div className="nav-links-container">
                                {/* Dashboard shortcut for logged-in users (left of ShowBiz) */}
                                {customer && (
                                    <a
                                        href="/dashboard"
                                        className={`nav-link nav-link-animated ${location.pathname === '/dashboard' ? 'active' : ''}`}
                                        onClick={(e) => {
                                            if (e.ctrlKey || e.metaKey || e.button === 1) return;
                                            e.preventDefault();
                                            handleNavigationClick('/dashboard');
                                        }}
                                    >
                                        Dashboard
                                    </a>
                                )}
                                {navigationLinks.map((link, index) => (
                                    <a
                                        key={index}
                                        href={link.path}
                                        className={`nav-link nav-link-animated ${location.pathname === link.path ? 'active' : ''}`}
                                        onClick={(e) => {
                                            // Allow Ctrl/Cmd+Click or middle click to open in new tab
                                            if (e.ctrlKey || e.metaKey || e.button === 1) {
                                                return; // Let default browser behavior handle it
                                            }
                                            e.preventDefault();
                                            handleNavigationClick(link.path);
                                        }}
                                        onMouseDown={(e) => {
                                            // Handle middle mouse button (button 1)
                                            if (e.button === 1) {
                                                e.preventDefault();
                                                window.open(link.path, '_blank', 'noopener,noreferrer');
                                            }
                                        }}
                                    >
                                        {link.name}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    

                    {/* Language + Auth (Profile/Login) - grouped on the right for desktop */}
                    {!isMobile && (
                        <div className="header-right-stack">
                            <div className="language-dropdown" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                                <button className="dropbtn">
                                    <img
                                        src="/images/language-globe-icon.png"
                                        alt="Lang"
                                    />
                                    <img
                                        src="/images/language-drow-icon.png"
                                        alt="Dropdown"
                                    />
                                </button>
                                {dropdownOpen && (
                                    <div 
                                        className="header-dropdown-content"
                                        onMouseEnter={handleMouseEnter}
                                        onMouseLeave={handleMouseLeave}
                                    >
                                        {languages.map(lang => (
                                            <a
                                                key={lang.code}
                                                href="#"
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    i18n.changeLanguage(lang.code);
                                                    setDropdownOpen(false);
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}
                                                className={i18n.language === lang.code ? 'active' : ''}
                                            >
                                                {lang.fullName}
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="header-auth-section" style={{ marginLeft: '8px' }}>
                                {customer ? (
                                    profileMenu
                                ) : (
                                    <button className="header-login-btn" onClick={handleLoginClick}>
                                        Login
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Mobile Menu Button and Language Dropdown - Grouped on Right */}
                    {isMobile && (
                        <div className="mobile-header-controls">
                            {/* Login/Profile Section - Mobile */}
                            {customer ? (
                                profileMenu
                            ) : (
                                <button className="header-login-btn header-login-btn-mobile" onClick={handleLoginClick}>
                                    Login
                                </button>
                            )}

                            {/* Language Dropdown - Mobile */}
                            <div className="language-dropdown-mobile">
                                <button
                                    className="dropbtn-mobile"
                                    onClick={toggleDropdown}
                                >
                                    <img
                                        src="/images/language-globe-icon.png"
                                        alt="Lang"
                                    />
                                    <img
                                        src="/images/language-drow-icon.png"
                                        alt="Dropdown"
                                    />
                                </button>
                                {dropdownOpen && (
                                    <div className="header-dropdown-content-mobile">
                                        {languages.map(lang => (
                                            <a
                                                key={lang.code}
                                                href="#"
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    i18n.changeLanguage(lang.code);
                                                    setDropdownOpen(false);
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    i18n.changeLanguage(lang.code);
                                                    setDropdownOpen(false);
                                                }}
                                                className={i18n.language === lang.code ? 'active' : ''}
                                            >
                                                {lang.fullName}
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                            
                            {/* Mobile Menu Button */}
                            {!isApplicationPage && (
                                <button
                                    className="navbar-toggler"
                                    type="button"
                                    onClick={toggleMobileMenu}
                                    aria-expanded={mobileMenuOpen}
                                    aria-label="Toggle navigation"
                                >
                                    <span className="navbar-toggler-icon"></span>
                                </button>
                            )}
                        </div>
                    )}

                    
                </div>

                {/* Mobile Navigation Menu */}
                {isMobile && mobileMenuOpen && !isApplicationPage && (
                    <div className="mobile-nav-menu">
                        <div className="mobile-nav-links">
                            {navigationLinks.map((link, index) => (
                                <a
                                    key={index}
                                    href={link.path}
                                    className={`mobile-nav-link mobile-nav-link-animated ${location.pathname === link.path ? 'active' : ''}`}
                                    onClick={(e) => {
                                        // Allow Ctrl/Cmd+Click or middle click to open in new tab
                                        if (e.ctrlKey || e.metaKey || e.button === 1) {
                                            return; // Let default browser behavior handle it
                                        }
                                        e.preventDefault();
                                        handleNavigationClick(link.path);
                                    }}
                                    onMouseDown={(e) => {
                                        // Handle middle mouse button (button 1)
                                        if (e.button === 1) {
                                            e.preventDefault();
                                            window.open(link.path, '_blank', 'noopener,noreferrer');
                                        }
                                    }}
                                >
                                    {link.name}
                                </a>
                            ))}
                        </div>
                    </div>
                )}
            </nav>

        {/* Fixed WhatsApp Button - rendered to body to avoid transform/overflow issues */}
        {!isApplicationPage && createPortal(
            <div
                ref={whatsappRef}
                className="whatsapp-fixed-button"
                onClick={handleWhatsAppClick}
                aria-label="Chat on WhatsApp"
                role="button"
            >
                <img
                    src={isMobile ? "/images/whatsapp-mobile-icon.png" : `${ASSETS_URL}images/whatsapp-mobile-icon.png`}
                    alt="WhatsApp"
                    className="whatsapp-fixed-icon"
                    draggable={false}
                />
            </div>,
            document.body
        )}

        {/* Login Modal */}
        {showLoginModal && (
            <div className="login-modal-overlay" onClick={() => setShowLoginModal(false)}>
                <div className="login-modal" onClick={(e) => e.stopPropagation()}>
                    <button className="login-modal-close" onClick={() => setShowLoginModal(false)}>
                        ×
                    </button>
                    <h2>Login to GrowBizh</h2>
                    <p>Enter your mobile number to access your applications</p>
                    <form onSubmit={handleLoginSubmit}>
                        <div className="login-form-group">
                            <input
                                type="tel"
                                id="login-mobile-input"
                                value={loginMobile}
                                onChange={(e) => setLoginMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                maxLength={10}
                                required
                                placeholder=" "
                            />
                            <label htmlFor="login-mobile-input">Enter 10-digit mobile number</label>
                        </div>
                        <button type="submit" className="login-submit-btn">
                            Send OTP
                        </button>
                    </form>
                </div>
            </div>
        )}

        {/* OTP Verification Modal */}
        <OtpVerificationModal
            show={showOtpModal}
            mobile={loginMobile}
            productType="UserLoginOtp"
            onClose={() => {
                setShowOtpModal(false);
                setLoginMobile('');
            }}
            onVerified={handleOtpSuccess}
        />
    </>
    );
}

export default Header;